import { Injectable, computed, signal } from '@angular/core';

/**
 * Shared cache of `value → label` mappings per filter key for the document-query
 * filter sidebar. The picker loads options paginated/searchable and keeps them
 * on `FilterOption.options`, but the "Selected" tab (and re-opening a picker)
 * needs labels for values that aren't in the current page. The loader pushes
 * `{ value, label, subLabel? }` pairs in after each load; the picker reads them
 * out, falling back to the raw value on a miss so the UI never breaks.
 *
 * Route-scoped (provided on the document-query route), so it resets on
 * module (re-)entry without leaking labels across sessions.
 */
@Injectable()
export class DocumentFilterLabelStore {
  private readonly store = signal<Record<string, Record<string, string>>>({});
  private readonly subStore = signal<Record<string, Record<string, string>>>({});

  /** Merge a fresh batch of `{ value, label, subLabel? }` entries for a filter key. */
  set(
    filterKey: string,
    entries: { value: string; label: string; subLabel?: string }[],
  ): void {
    if (entries.length === 0) return;
    this.store.update((s) => {
      const existing = s[filterKey] ?? {};
      const next: Record<string, string> = { ...existing };
      for (const e of entries) next[e.value] = e.label;
      return { ...s, [filterKey]: next };
    });
    const subEntries = entries.filter(
      (e): e is { value: string; label: string; subLabel: string } =>
        typeof e.subLabel === 'string' && e.subLabel.length > 0,
    );
    if (subEntries.length === 0) return;
    this.subStore.update((s) => {
      const existing = s[filterKey] ?? {};
      const next: Record<string, string> = { ...existing };
      for (const e of subEntries) next[e.value] = e.subLabel;
      return { ...s, [filterKey]: next };
    });
  }

  /** Resolve a single value to its cached label, or `null` if not seen yet. */
  resolve(filterKey: string, value: string): string | null {
    return this.store()[filterKey]?.[value] ?? null;
  }

  /** Resolve a single value to its cached sub-label, or `null`. */
  resolveSub(filterKey: string, value: string): string | null {
    return this.subStore()[filterKey]?.[value] ?? null;
  }

  /** Reactive accessor — re-renders once labels stream in. */
  readonly snapshot = computed(() => this.store());

  readonly subLabelSnapshot = computed(() => this.subStore());
}
